/** Sign-in with Google / Apple / Telegram — docs/superpowers/specs/2026-10-01-social-login-design.md */
import { z } from 'zod';
import { zLocale } from './common';
import { zEmail, type Me } from './auth';

export const AUTH_PROVIDERS = ['google', 'apple', 'telegram'] as const;
export type AuthProviderId = (typeof AUTH_PROVIDERS)[number];
export const zAuthProvider = z.enum(AUTH_PROVIDERS);

/**
 * `start?mode=` — sign in / sign up, link the provider to the signed-in account, or confirm the signed-in player
 * with a linked provider (`reauth`: an account without a password before deleting it / setting a password)
 */
export const OAUTH_MODES = ['login', 'link', 'reauth'] as const;
export type OAuthMode = (typeof OAUTH_MODES)[number];

/** GET /api/auth/providers — only the providers configured on the server, in display order */
export type ProviderInfo = { id: 'google' } | { id: 'apple' } | { id: 'telegram'; botId: number; botName: string };
export interface ProvidersResult {
  providers: ProviderInfo[];
}

/** GET /api/auth/oauth/:provider/start query */
export const zOAuthStart = z.object({ mode: z.enum(OAUTH_MODES).default('login'), lang: zLocale.default('en') }).strict();

/**
 * The object Telegram's Login Widget hands to the callback (`Telegram.Login.auth`). Every field takes part in the
 * hash check, so unknown fields are kept (passthrough) — Telegram may add some.
 */
export const zTelegramAuthData = z
  .object({
    id: z.number().int().positive(),
    first_name: z.string().max(256).optional(),
    last_name: z.string().max(256).optional(),
    username: z.string().max(64).optional(),
    photo_url: z.string().max(1024).optional(),
    auth_date: z.number().int().positive(),
    hash: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .passthrough();
export type TelegramAuthData = z.infer<typeof zTelegramAuthData>;

/** POST /api/auth/oauth/telegram */
export const zTelegramAuth = z
  .object({
    auth: zTelegramAuthData,
    mode: z.enum(OAUTH_MODES).default('login'),
    locale: zLocale.default('en'),
    /** sign-up device traits for the invite check (as in POST /api/auth/register) */
    device: z.unknown().optional(),
  })
  .strict();
export type TelegramAuthInput = z.input<typeof zTelegramAuth>;
/** POST /api/auth/oauth/telegram response */
export interface TelegramAuthResult {
  me: Me;
  /** a new account was created (the client suggests changing the nickname) */
  created: boolean;
  /** signing in cancelled a pending account deletion */
  deletionCancelled?: true;
}

/** One linked provider account (profile → "Ways to sign in") */
export interface LinkedIdentity {
  provider: AuthProviderId;
  /** as given by the provider (may be an Apple relay address); null for Telegram */
  email: string | null;
  /** Telegram @username or the name the provider gave, for the list; null if none */
  label: string | null;
  createdAt: string;
}
/** GET /api/me/identities, DELETE /api/me/identities/:provider response */
export interface IdentitiesResult {
  /** the account can sign in with e-mail + password */
  password: boolean;
  identities: LinkedIdentity[];
  /**
   * this session: until when the last provider confirmation (`mode=reauth`) counts — deleting an account without a
   * password needs it; null if none or expired
   */
  reauthUntil: string | null;
  /** this session: until when it counts as fresh (signed in or confirmed) for setting a password; null if not */
  freshUntil: string | null;
}

/** POST /api/me/email — only for an account without an e-mail (signed up with Telegram); sends the confirmation letter */
export const zSetEmail = z.object({ email: zEmail }).strict();
export type SetEmailInput = z.input<typeof zSetEmail>;
